import React from 'react';
import {
    IApplication,
    IApplicationInternal,
    IScope,
    IApplicationToken
} from '../Interfaces/interfaces';
import {IManagementStore} from "Stores/Management/types";
import {IAuthStore} from "Stores/Auth/types";
import {IValuesStore} from "Stores/Values/types";
import {IApplicationsStore} from "Stores/Applications/types";
import {inject, observer} from "mobx-react";
import {observable} from "mobx";
import {FullWidthFlexBox} from '@src/Shared/Components/FullWidthFlexBox';
import {ItemListPageContentWrapper, ItemListPageTabWrapper} from '@src/Shared/Layout/styles';
import {Tab, Tabs} from "@material-ui/core";
import {AddButton} from "@src/Shared/Components/AddButton/AddButton";
import {InternalAppDialog} from './InternalAppDialog/InternalAppDialog';
import {Values} from "Tools/Values";
import {RemoveInternalAppDialog} from './RemoveInternalAppDialog/RemoveInternalAppDialog';
import {ExternalAppDialog} from './ExternalAppDialog/ExternalAppDialog';
import AppListTab from './AppListTab/AppListTab';
import { InternalAppDetailsDialog } from './InternalAppDetailsDialog/InternalAppDetailsDialog';

interface IProps {
    management?: IManagementStore;
    auth?: IAuthStore;
    values?: IValuesStore;
    applicationsStore?: IApplicationsStore;
}

@inject("auth", "values", "management", "applicationsStore")
@observer
class ApplicationCatalogue extends React.Component<IProps> {
    @observable externalApps: IApplication[] = [];
    @observable internalApps: IApplicationInternal[] = [];
    @observable scopes: IScope[] = [];
    @observable tabIndex: number = 0;

    @observable selectedExternalApp: IApplication = null;
    @observable selectedInternalApp: IApplicationInternal = null;
    @observable isEdit: boolean = false;

    @observable showExternalAppDialog: boolean = false;
    @observable showInternalAppDialog: boolean = false;
    @observable showInternalAppDetailsDialog: boolean = false;
    @observable showRemoveAppDialog: boolean = false;

    isAdmin = this.props.auth.user.roles.find(x => x == this.props.values.roles.Administrator) != null;

    componentDidMount = async () => {
        const {applications, internalApplications} = await this.props.applicationsStore.getApplications();

        this.externalApps = applications;
        this.internalApps = internalApplications;
        this.scopes = await this.props.applicationsStore.getScopes();
    }

    async handleExternalAppActivation() {
        const isActivate = !this.selectedExternalApp.isEnabled;
        const id = this.selectedExternalApp.clientId;
        const result = await this.props.applicationsStore.changeApplicationStatus(id, isActivate);

        if (result) {
            const index = this.externalApps?.findIndex(x => x.clientId == id);
            this.externalApps[index].isEnabled = isActivate;
            this.selectedExternalApp.isEnabled = isActivate;
        }
    }

    handleCloseExternalAppDialog() {
        this.showExternalAppDialog = false;
        this.selectedExternalApp = null;
    }

    handleInternalAppDialog(id: string){
        this.selectedInternalApp = this.internalApps.find(app => app.clientId === id);

        if (this.isAdmin) {
            this.isEdit = true;
            this.showInternalAppDialog = true;
        } else {
            this.showInternalAppDetailsDialog = true;
        }
    }

    handleAddInternalAppDialog() {
        this.isEdit = false;
        this.showInternalAppDialog = true;
    }

    handleOpenExternalAppDialog(id: string) {
        this.selectedExternalApp = this.externalApps.find(app => app.clientId === id);
        this.showExternalAppDialog = true;
    }

    handleCloseInternalAppDialog() {
        this.isEdit = false;
        this.selectedExternalApp = null;
        this.showInternalAppDialog = false;
    }

    handleCloseInternalAppDetailsDialog() {
        this.selectedInternalApp = null;
        this.showInternalAppDetailsDialog = false;
    }

    handleConfirmInternalAppDialog = (app: IApplicationInternal) => {
        if (this.isEdit) {
            const index = this.internalApps.findIndex(x => x.clientId === app.clientId);
            this.internalApps[index] = app;
        } else {
            this.internalApps.push(app);
        }

        this.handleCloseInternalAppDialog();
    }

    handleRefreshInternalAppToken(newToken: IApplicationToken) {
        const index = this.internalApps.findIndex(app => app.clientId === this.selectedInternalApp.clientId);

        this.internalApps[index].token = newToken.token;
        this.internalApps[index].expiresAt = newToken.expiresAt;
    }

    handleRemoveInternalAppDialog(id: string) {
        this.selectedInternalApp = this.internalApps.find(app => app.clientId === id);
        this.showRemoveAppDialog = true;
    }

    handleCloseRemoveInternalAppDialog() {
        this.showRemoveAppDialog = false;
        this.selectedInternalApp = null;
    }

    async handleRemoveInternalApp() {
        const id = this.selectedInternalApp.clientId;
        const isRemoved = await this.props.applicationsStore.removeInternalApplication(id);

        if (isRemoved) {
            this.internalApps = this.internalApps.filter(app => app.clientId !== id);
            this.handleCloseRemoveInternalAppDialog();
        }
    }

    render() {
        return (
            <>
                <FullWidthFlexBox>
                    <ItemListPageTabWrapper>
                        <Tabs
                            value={this.tabIndex}
                            onChange={(e, index) => this.tabIndex = index}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="standard"
                        >
                            <Tab label={Values.getLocalizationValue('installedApps')}/>
                            <Tab label={Values.getLocalizationValue('availableApps')}/>
                        </Tabs>
                    </ItemListPageTabWrapper>

                    {(this.tabIndex === 0 && this.isAdmin) &&
                        <AddButton
                            onClick={() => this.handleAddInternalAppDialog()}
                            text={Values.getLocalizationValue('addNew')}
                        />
                    }
                </FullWidthFlexBox>


                <ItemListPageContentWrapper>
                    {this.tabIndex === 0 ? (
                        <AppListTab
                            externalApps={this.externalApps.filter(x => x.isEnabled)}
                            internalApps={this.internalApps}
                            isEnabledApps={true}
                            editExternalApp={(id) => this.handleOpenExternalAppDialog(id)}
                            editInternalApp={(id) => this.handleInternalAppDialog(id)}
                            removeInternalApp={(id) => this.handleRemoveInternalAppDialog(id)}
                        />
                    ) : (
                        <AppListTab
                            externalApps={this.externalApps.filter(x => !x.isEnabled)}
                            isEnabledApps={false}
                            editExternalApp={(id) => this.handleOpenExternalAppDialog(id)}
                        />
                    )}
                </ItemListPageContentWrapper>

                {this.showInternalAppDialog && (
                    <InternalAppDialog
                        app={this.selectedInternalApp}
                        isOpened={this.showInternalAppDialog}
                        isEdit={this.isEdit}
                        scopes={this.scopes}
                        onClose={() => this.handleCloseInternalAppDialog()}
                        onConfirm={(app) => this.handleConfirmInternalAppDialog(app)}
                        onTokenRefresh={(token) => this.handleRefreshInternalAppToken(token)}
                    />
                )}

                {this.showInternalAppDetailsDialog && (
                    <InternalAppDetailsDialog
                        app={this.selectedInternalApp}
                        isOpened={this.showInternalAppDetailsDialog}
                        onClose={() => this.handleCloseInternalAppDetailsDialog()}
                    />
                )}

                {this.showRemoveAppDialog && (
                    <RemoveInternalAppDialog
                        isOpened={this.showRemoveAppDialog}
                        onSubmit={() => this.handleRemoveInternalApp()}
                        onClose={() => this.handleCloseRemoveInternalAppDialog()}
                    />
                )}

                {this.showExternalAppDialog && (
                    <ExternalAppDialog
                        onClose={() => this.handleCloseExternalAppDialog()}
                        app={this.selectedExternalApp}
                        isOpened={this.showExternalAppDialog}
                        isAdmin={this.isAdmin}
                        onActivationButtonClick={() => this.handleExternalAppActivation()}
                    />
                )}
            </>
        )
    }
}

export default ApplicationCatalogue;
